import { Piano, type PianoEvent } from "@/lib/piano/piano";
import { Lane } from "../lane";
import { LocalTransport } from "../local-transport";
import { detectChord } from "@/lib/music/chord";
import { NoteCluster, NoteSpan } from "@/lib/music/part";

/**
 * Le clavier du joueur, et où ce qu'il joue s'écrit.
 *
 * # Il n'est plus un contrôleur de piste
 *
 * Il fabriquait sa lane et la portait, ce qui en faisait un `Controler` comme
 * un autre. Il ne l'est plus : le clavier appartient au **poste**, il survit
 * aux bascules de session, alors que ce qu'on y joue appartient à la
 * **session**. Confondre les deux, c'était emporter dans sa session solo
 * suivante ce qu'on venait de jouer en séance.
 *
 * La session lui tend donc une lane en entrant en scène (`useLane`), la
 * reprend en sortant. Entre les deux, il écrit dedans. Sans lane, les touches
 * sonnent toujours — c'est le piano qui les sonne — mais rien ne s'inscrit.
 *
 * # Où il capte dépend de qui écoute
 *
 * Le tick d'un geste se lit à deux endroits possibles du transport, et le bon
 * dépend de ce que le poste fait de ses gestes — voir `setEmitting`. Le
 * clavier ne sait pas pourquoi ; il sait seulement s'il émet.
 */
export class PianoControler {
  private lane?: Lane;
  private unsubscribePiano?: () => void;
  private unsubscribeTransport?: () => void;
  private activeNotes = new Map<number, NoteSpan>();
  private currentCluster: NoteCluster | null = null;

  /**
   * Ce poste émet-il ses gestes vers une séance. Voir `setEmitting`.
   *
   * Faux par défaut : hors séance personne n'écoute, et le délai de
   * restitution est nul — les deux points de capture coïncident, le choix ne
   * se voit pas. Il ne se voit qu'en séance, où le contexte live le pose
   * d'après l'état publié ; et sans état, personne n'émet.
   */
  private emitting = false;

  constructor(private readonly piano: Piano, private readonly transport: LocalTransport) {}

  /**
   * Dit au clavier si ce poste émet, ce qui décide **où il capte**.
   *
   * Un émetteur capte à sa tête de lecture plus le délai de restitution
   * (`captureTick`) : son tick doit être encore devant chez le destinataire
   * à l'arrivée, et c'est le transport qui porte le délai, lui seul. Une vue
   * déplacée à la molette capte là où elle est, comme hors séance. Le leader
   * n'a pas de cas particulier : son délai vaut zéro, les deux ticks
   * coïncident.
   *
   * Un auditeur capte à sa tête de lecture (`currentTick`). Son tick ne part
   * chez personne, et capter en avance lui ferait entendre son propre jeu
   * avec son propre décalage — pour rien. À la tête, il s'entend à l'instant,
   * et joue avec ce qu'il entend, qui est la tête aussi.
   */
  setEmitting(emitting: boolean): void {
    this.emitting = emitting;
  }

  start(): void {
    this.unsubscribeTransport = this.transport.on((e) => {
      if (e.type === 'detach') {
        this.lane?.disable();
      }

      if (e.type === 'play') {
        this.lane?.enable();
      }
    });

    this.unsubscribePiano = this.piano.on((e) => {
      // Les indications n'ont ni tick ni origine : rien à écrire.
      if (e.type === "hints_changed") {
        return;
      }

      // Cette lane est le jeu du joueur, et rien d'autre. Ce que l'application
      // presse elle-même — aperçu de voicing, pratique d'accords — n'a pas à
      // s'inscrire sur le roll ni à nourrir la détection d'accords. Mais ça doit
      // sonner : le son passe par la lane, et un geste filtré avant elle
      // n'arrivait plus à aucun synthé. Les aperçus étaient muets.
      if (e.origin !== "player") {
        this.soundWithoutWriting(e);

        return;
      }

      const lane = this.lane;

      if (lane === undefined) {
        return;
      }

      // Un émetteur capte à sa tête plus le délai de restitution : en séance,
      // la tête court derrière la position de séance de ce délai, et un tick
      // capté sous elle serait déjà passé chez le partenaire à l'arrivée. Un
      // auditeur, lui, capte sous la tête : rien ne part, et il s'entend à
      // l'instant. Voir `setEmitting`.
      //
      // Et à l'instant du geste, quand la source le connaît : un message MIDI
      // est horodaté à sa réception par le navigateur, une image avant que ce
      // gestionnaire tourne quand le roll est en train de rendre. Sans ça le
      // tick portait l'attente du fil principal, et le partenaire posait la
      // note là où on avait pu s'en occuper, pas là où elle avait été jouée.
      const tick = this.emitting ? this.transport.captureTick(e.at) : this.transport.currentTick(e.at);

      if (e.type === "press") {
        lane.noteOn(e.note, tick, e.velocity, e.at);
        this.activeNotes.set(e.note.toMidi(), {
          tick, durationTicks: 0, velocity: e.velocity, note: e.note,
        });
        this.pushLiveCluster(tick);
      } else if (e.type === "release") {
        lane.noteOff(e.note, tick, e.at);
        this.activeNotes.delete(e.note.toMidi());
        this.pushLiveCluster(tick);
      } else if (e.type === "sustain_on") {
        lane.sustainOn(tick, e.at);
      } else if (e.type === "sustain_off") {
        lane.sustainOff(tick, e.at);
      }
    });
  }

  /**
   * Dirige le clavier vers la lane d'une session, ou nulle part.
   *
   * L'accord en cours de frappe ne traverse pas la bascule : il a été ouvert
   * dans l'autre lane, et le refermer ici l'écrirait deux fois.
   */
  useLane(lane: Lane | null): void {
    this.activeNotes.clear();
    this.currentCluster = null;
    this.lane = lane ?? undefined;

    if (lane === null) {
      return;
    }

    if (this.transport.isDetached) {
      lane.disable();
    } else {
      lane.enable();
    }
  }

  getLane(): Lane | undefined {
    return this.lane;
  }

  /**
   * Drop everything played so far.
   *
   * The live lane is cumulative by design — in free play, seeing what you
   * have just played is the point. Inside the learning journey it is not:
   * every pass drew on top of the last, so by the third attempt the roll
   * carried three ghost performances in a grey that sits at **1.03:1**
   * against the left hand — the same object, in luminance — and the player
   * was reading their own history instead of the notation.
   */
  clearLive(): void {
    this.lane?.clearNotes();
    this.activeNotes.clear();
    this.currentCluster = null;
  }

  /** Combien de frappes la lane courante tient. Zéro sans lane. */
  strikeCount(): number {
    return this.lane?.getNotesCount() ?? 0;
  }

  /**
   * Fait sonner un geste de l'application sur le synthé de la lane, sans rien
   * y écrire.
   *
   * Directement sur le synthé et non par `noteOn` : la lane émet ce qu'on y
   * écrit, et ce qu'elle émet part en séance. Un aperçu n'a rien à y faire.
   * Muette, la lane le reste pour l'application aussi.
   */
  private soundWithoutWriting(e: PianoEvent): void {
    const lane = this.lane;

    if (lane === undefined || lane.isMuted()) {
      return;
    }

    if (e.type === "press") {
      lane.synth.noteOn(e.note.toMidi(), e.velocity);
    } else if (e.type === "release") {
      lane.synth.noteOff(e.note.toMidi());
    }
  }

  private pushLiveCluster(tick: number): void {
    const lane = this.lane;

    if (lane === undefined) {
      return;
    }

    if (this.activeNotes.size === 0) {
      // All notes released → close the current cluster
      if (this.currentCluster) {
        this.currentCluster.endTick = tick;
        this.currentCluster = null;
      }

      return;
    }

    const notes = [...this.activeNotes.values()];
    const chord = detectChord(notes.map(n => n.note.toMidi()));

    if (this.currentCluster) {
      // Mutate in place, same ref in Lane, no new cluster
      this.currentCluster.notes = notes;
      this.currentCluster.chord = chord;
      lane.notifyClusterChange();

      return;
    }

    // First note after silence → new cluster
    const cluster: NoteCluster = {
      startTick: tick, endTick: Infinity, notes, chord,
    };
    lane.addCluster(cluster);
    this.currentCluster = cluster;
  }

  destroy(): void {
    this.unsubscribePiano?.();
    this.unsubscribeTransport?.();
    this.lane = undefined;
  }
}
